import { AlertTriangle, Eye, EyeOff, GitBranch, Globe, Maximize2, PauseCircle, PlayCircle, RotateCcw, Save, Tags } from "lucide-react";
import type { HealthMapSourceFilter, HealthMapStatusFilter, HealthMapViewControls } from "./healthMapUtils";
import { healthMapSourceFilterOptions, healthMapStatusFilterOptions } from "./healthMapUtils";

interface HealthMapControlsProps {
  controls: HealthMapViewControls;
  nodeCount: number;
  edgeCount: number;
  isRotating: boolean;
  onChange: (controls: HealthMapViewControls) => void;
  onFitView: () => void;
  onResetLayout: () => void;
  onSaveLayout: () => void;
  onToggleRotation: () => void;
}

export function HealthMapControls({
  controls,
  nodeCount,
  edgeCount,
  isRotating,
  onChange,
  onFitView,
  onResetLayout,
  onSaveLayout,
  onToggleRotation
}: HealthMapControlsProps) {
  function update(nextControls: Partial<HealthMapViewControls>) {
    onChange({
      ...controls,
      ...nextControls
    });
  }

  return (
    <div className="health-map-toolbar" aria-label="Health map controls">
      <div className="health-map-filter-group">
        <label>
          <span>Source</span>
          <select
            value={controls.sourceFilter}
            onChange={(event) => update({ sourceFilter: event.target.value as HealthMapSourceFilter })}
          >
            {healthMapSourceFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Status</span>
          <select
            value={controls.statusFilter}
            onChange={(event) => update({ statusFilter: event.target.value as HealthMapStatusFilter })}
          >
            {healthMapStatusFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <span className="health-map-count-pill">
          {nodeCount} nodes / {edgeCount} edges
        </span>
      </div>

      <div className="health-map-action-group">
        <button className="ghost-button health-map-action-button" type="button" onClick={onFitView}>
          <Maximize2 size={14} aria-hidden="true" />
          Fit View
        </button>
        <button className="ghost-button health-map-action-button" type="button" onClick={onResetLayout}>
          <RotateCcw size={14} aria-hidden="true" />
          Reset Layout
        </button>
        <button className="ghost-button health-map-action-button" type="button" onClick={onSaveLayout}>
          <Save size={14} aria-hidden="true" />
          Save Layout
        </button>
        <button
          className={`toggle-button health-map-action-button ${controls.showLabels ? "active" : ""}`}
          type="button"
          aria-pressed={controls.showLabels}
          onClick={() => update({ showLabels: !controls.showLabels })}
        >
          <Tags size={14} aria-hidden="true" />
          Labels
        </button>
        <button
          className={`toggle-button health-map-action-button ${controls.showEdges ? "active" : ""}`}
          type="button"
          aria-pressed={controls.showEdges}
          onClick={() => update({ showEdges: !controls.showEdges })}
        >
          {controls.showEdges ? <GitBranch size={14} aria-hidden="true" /> : <EyeOff size={14} aria-hidden="true" />}
          Edges
        </button>
        <button
          className={`toggle-button health-map-action-button ${controls.unhealthyOnly ? "active" : ""}`}
          type="button"
          aria-pressed={controls.unhealthyOnly}
          onClick={() => update({ unhealthyOnly: !controls.unhealthyOnly })}
        >
          {controls.unhealthyOnly ? <AlertTriangle size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
          Unhealthy Only
        </button>
        <button
          className={`toggle-button health-map-action-button ${controls.layoutMode === "sphere" ? "active" : ""}`}
          type="button"
          aria-pressed={controls.layoutMode === "sphere"}
          onClick={() => update({ layoutMode: controls.layoutMode === "sphere" ? "grid" : "sphere" })}
        >
          <Globe size={14} aria-hidden="true" />
          Sphere
        </button>
        {controls.layoutMode === "sphere" && (
          <button
            className={`toggle-button health-map-action-button ${isRotating ? "active" : ""}`}
            type="button"
            aria-pressed={isRotating}
            onClick={onToggleRotation}
          >
            {isRotating
              ? <PauseCircle size={14} aria-hidden="true" />
              : <PlayCircle size={14} aria-hidden="true" />}
            Rotate
          </button>
        )}
      </div>
    </div>
  );
}
